// Fees, complexity, par time, stars, tips, rep — and the contract board.

import type { Contract, EquipmentModel, Layout, LocationGroup, MutationKey, TaskKind, TaskSpec } from './types';
import { buildGrid, cellAt, isSolidType, layoutStats, T_PAVED } from './layout';
import { contractLayout } from './mutations';
import { MODELS, ownedOfKind, repLevel, type SaveGame } from './economy';
import { mixSeed, mulberry32, pick, randInt, shuffled, type Rng } from './rng';
import { storyFor } from './stories';
import { bedStartLeaves, blowStartPct } from './spawn';
import { visitOf, visitsOf } from './revisits';
import { withNeglect } from './vibe';

export const LOCATION_ORDER: readonly LocationGroup[] = ['backyards', 'nature-strips', 'ovals', 'clubs', 'parks', 'rural'];

export const RATES_AUD_M2 = {
  backyard: 1.5, natureStrip: 0.85, oval: 0.18, bowls: 4.5, cemetery: 1.2, rural: 0.12, caravan: 1.1,
} as const;

export const TASK_ADDON_AUD: Record<TaskKind, number> = { mow: 0, trim: 25, blow: 20, collect: 40, 'ground-check': 10 };
export const OBSTACLE_PENALTY_AUD = 15;
export const VIOLATION_PENALTY_AUD = 50;
/** Per ground object run over with the blades on ("Mulched the {kind}"). */
export const MULCH_PENALTY_AUD = 5;
export const REP_FOR_STARS = [0, 10, 20, 35] as const;
export const TASK_TARGETS: Record<TaskKind, number> = { mow: 95, trim: 85, blow: 90, collect: 80, 'ground-check': 100 };

/**
 * A$/m² for a layout. Keyword/feature matches first (bowls green, cemetery,
 * caravan park), then the location group.
 */
export function rateFor(layout: Layout): number {
  const text = `${layout.key} ${layout.title}`.toLowerCase();
  if (text.includes('bowls') || layout.zones.some((z) => z.id === 'green')) return RATES_AUD_M2.bowls;
  if (text.includes('cemetery') || layout.props.some((p) => p.kind === 'headstone')) return RATES_AUD_M2.cemetery;
  if (text.includes('caravan') || layout.props.some((p) => p.kind === 'annexe' || p.kind === 'camp-kitchen')) return RATES_AUD_M2.caravan;
  switch (layout.locationGroup) {
    case 'backyards': return RATES_AUD_M2.backyard;
    case 'nature-strips': return RATES_AUD_M2.natureStrip;
    case 'ovals': return RATES_AUD_M2.oval;
    case 'clubs': return RATES_AUD_M2.oval;
    case 'parks': return RATES_AUD_M2.cemetery;
    case 'rural': return RATES_AUD_M2.rural;
  }
  return RATES_AUD_M2.backyard;
}

/**
 * Share of the area fee a list with no mowing pays (P3 tidy-ups and
 * clearances). The area × rate term is the mowing price; without it an oval
 * tidy-up billed the full A$1,900 oval fee for a walk round the boundary
 * (balance pass, docs/balance.md). Lists with a mow task are unchanged.
 */
export const NO_MOW_AREA_SHARE = 0.15;

/**
 * area_m² × rate + task add-ons, rounded to whole dollars. area = (mowable + trim) grass cells × cellM².
 * A list without a mow task pays NO_MOW_AREA_SHARE of the area term.
 */
export function feeFor(layout: Layout, tasks: readonly TaskSpec[]): number {
  const area = layoutStats(layout).areaM2;
  const mows = tasks.length === 0 || tasks.some((t) => t.kind === 'mow');
  let fee = area * rateFor(layout) * (mows ? 1 : NO_MOW_AREA_SHARE);
  for (const t of tasks) fee += TASK_ADDON_AUD[t.kind] ?? 0;
  return Math.round(fee);
}

/**
 * Complexity ★ = clamp(round(1 + solidProps/5 + (tasks − 1) × 0.5
 *                 + max(0, log10(area_m² / 100)) × 0.5), 1, 5).
 * A bare nature strip is 1★, a busy backyard with three tasks ~3★, a footy
 * oval with four tasks 5★.
 */
export function complexityOf(layout: Layout, tasks: readonly TaskSpec[]): 1 | 2 | 3 | 4 | 5 {
  const s = layoutStats(layout);
  const raw = 1 + s.solidProps / 5 + (Math.max(1, tasks.length) - 1) * 0.5 + Math.max(0, Math.log10(Math.max(1, s.areaM2) / 100)) * 0.5;
  return Math.min(5, Math.max(1, Math.round(raw))) as 1 | 2 | 3 | 4 | 5;
}

/**
 * How much a layout's gameScale boosts a mower's deck and speed. Ride-ons and
 * zero-turns get it all (an oval on a Rider is 8–12 min); walk-behind mowers
 * get √gameScale, as the walker does (balance pass, "ride-on timing"): a push
 * mower on an oval is honest slow going, not a 17 km/h jog.
 */
export function mowerScale(model: Pick<EquipmentModel, 'rideOn'>, gameScale = 1): number {
  return model.rideOn ? gameScale : Math.sqrt(gameScale);
}

/** Deck width in cells for a mower on a layout (with its share of gameScale, mowerScale). */
export function deckCellsFor(model: EquipmentModel, cellM: number, gameScale = 1): number {
  return Math.max(1, Math.round(((model.deckCm ?? 46) / 100) * mowerScale(model, gameScale) / cellM));
}

/** Top speed in cells per second (with its share of gameScale, mowerScale). */
export function speedCellsPerS(model: EquipmentModel, cellM: number, gameScale = 1): number {
  return ((model.speedKmh ?? 4) / 3.6) * mowerScale(model, gameScale) / cellM;
}

// ─── Balance pass: ovals need a ride-on ─────────────────────────────────

/** Location groups whose contracts need a ride-on (or zero-turn) in the shed. */
export const RIDE_ON_GROUPS: readonly LocationGroup[] = ['ovals'];
export const NEEDS_RIDE_ON_COPY = 'Needs a ride-on';

/** Does this contract need a ride-on? (Its location group, from minRepLevel as the board sets it.) */
export function needsRideOn(c: Pick<Contract, 'minRepLevel'>): boolean {
  const g = LOCATION_ORDER[c.minRepLevel - 1];
  return !!g && RIDE_ON_GROUPS.includes(g);
}

/** Does the shed own a ride-on or zero-turn? */
export function ownsRideOn(save: Pick<SaveGame, 'owned'>, models: Record<string, EquipmentModel> = MODELS): boolean {
  return save.owned.some((o) => models[o.modelKey]?.kind === 'mower' && !!models[o.modelKey]?.rideOn);
}

/** On the board but locked: an oval with no ride-on in the shed ("Needs a ride-on"). */
export function rideOnLocked(c: Pick<Contract, 'minRepLevel'>, save: Pick<SaveGame, 'owned'>, models: Record<string, EquipmentModel> = MODELS): boolean {
  return needsRideOn(c) && !ownsRideOn(save, models);
}

export interface ParInput {
  mowableCells: number;
  trimCells: number;
  leafCount: number;
  cellM: number;
  gameScale?: number;
  /** Ground objects to pick up (counted only with a ground-check task). */
  groundObjects?: number;
  /** Long side (cells) of the lawn's bounding box — objects spread along it (SimGrid.spanCells). */
  spanCells?: number;
  /** Weeds on paved cells: the trim task counts them alongside trim cells. */
  weedCells?: number;
}

/** Seconds per trim target (trim cell or paved weed) for the trim task. */
export const PAR_TRIM_S = 0.35;

/**
 * Par seconds = mowable / (deck_cells × speed_cells_per_s) × 1.6
 *             + (trim_cells + paved_weeds) × 0.35       (only with a trim task)
 *             + leaves × 0.05                           (only with a blow or collect task)
 *             + pickup_cells / speed_cells_per_s       (only with a ground-check task),
 * pickup_cells = max(√(objects × lawn_cells), span × objects/(objects + 1)):
 * a tour through N scattered objects — √(N·A) on an open lawn, and on a thin
 * strip at least the trip from one end to the farthest of N objects along
 * its long side. lawn_cells = mowable + trim. Uses the mower BROUGHT. Without
 * `tasks`, every term counts. The brief's formula had neither the paved-weed
 * nor the pickup term (DECISIONS.md): on weedy, spread-out nature strips a
 * fair run came in at 1.3–1.4 × par.
 */
export function parSeconds(p: ParInput, mower: EquipmentModel, tasks?: readonly TaskSpec[]): number {
  const gs = p.gameScale ?? 1;
  const has = (k: TaskKind) => !tasks || tasks.some((t) => t.kind === k);
  const speed = speedCellsPerS(mower, p.cellM, gs);
  let par = 0;
  if (has('mow')) par += (p.mowableCells / (deckCellsFor(mower, p.cellM, gs) * speed)) * 1.6;
  if (has('trim')) par += (p.trimCells + (p.weedCells ?? 0)) * PAR_TRIM_S;
  if (has('blow') || has('collect')) par += p.leafCount * 0.05;
  const n = p.groundObjects ?? 0;
  if (has('ground-check') && n > 0) {
    const tour = Math.max(Math.sqrt(n * (p.mowableCells + p.trimCells)), ((p.spanCells ?? 0) * n) / (n + 1));
    par += tour / speed;
  }
  return par;
}

/** Ground objects a contract spawns: complexity + 1 with a ground-check task (+1 for a lost ball). */
export function groundObjectsFor(layout: Layout, tasks: readonly TaskSpec[], lostBall = false): number {
  if (!tasks.some((t) => t.kind === 'ground-check')) return 0;
  return complexityOf(layout, tasks) + 1 + (lostBall ? 1 : 0);
}

/**
 * ParInput for an estimate (contract board, gear screen) from a contract's
 * layout: mean leaf count, expected paved weeds (paved × weedFraction).
 * The sim uses the job's actual leaves, weeds and objects.
 */
export function estimateParInput(layout: Layout, tasks: readonly TaskSpec[], lostBall = false): ParInput {
  const s = layoutStats(layout);
  return {
    mowableCells: s.mowableCells,
    trimCells: s.trimCells,
    leafCount: (layout.leaves.count[0] + layout.leaves.count[1]) / 2,
    cellM: layout.cellM,
    gameScale: layout.gameScale,
    groundObjects: groundObjectsFor(layout, tasks, lostBall),
    spanCells: s.spanCells,
    weedCells: s.pavedCells * layout.weedFraction,
  };
}

/** 3★ ≤ par, 2★ ≤ 1.4 × par, 1★ finished slower, 0★ abandoned. */
export function starsFor(seconds: number, par: number, abandoned = false): 0 | 1 | 2 | 3 {
  if (abandoned) return 0;
  if (seconds <= par) return 3;
  if (seconds <= par * 1.4) return 2;
  return 1;
}

/** Tip % = clamp(round(thoroughness − 90) × 4, 0, 50). */
export function tipPct(thoroughness: number): number {
  return Math.min(50, Math.max(0, Math.round(thoroughness - 90) * 4));
}

export function repForStars(stars: number): number {
  return REP_FOR_STARS[Math.max(0, Math.min(3, Math.round(stars)))];
}

export function unlockedGroups(repPoints: number): LocationGroup[] {
  return LOCATION_ORDER.slice(0, repLevel(repPoints));
}

export function taskLabel(kind: TaskKind, layout: Layout, objects = 0): string {
  switch (kind) {
    case 'mow': return `Mow to ${layout.rangeMm[0]}–${layout.rangeMm[1]} mm`;
    case 'trim': return 'Whipper-snip the edges';
    case 'blow': return 'Blow the paths clear';
    case 'collect': return 'Tarp and bag the leaves';
    case 'ground-check': return objects > 0 ? `Ground check: ${objects} items` : 'Ground check';
  }
}

function makeTask(kind: TaskKind, layout: Layout, objects = 0): TaskSpec {
  return { kind, target: TASK_TARGETS[kind], label: taskLabel(kind, layout, objects) };
}

const GENERIC_BLURBS = [
  "Kikuyu's gone feral out the back.",
  'Owner says it was only done a fortnight ago. It was not.',
  'Tidy it up before the in-laws land Saturday.',
  "Real estate photos Monday. Make it look like a brochure.",
  'Straight lines, please. The neighbour notices.',
  "Dog's friendly. Mostly. Gate latch sticks.",
  'Rental inspection Thursday. Tenant is very keen.',
  "It's been a wet month. The grass has noticed.",
  'Quick one. Cash in the letterbox.',
  "Last bloke scalped it. Don't be the last bloke.",
  "Mind the sprinkler heads. They're everywhere.",
  'Kids want the cricket pitch back by arvo.',
];

const MUTATION_BLURBS: Partial<Record<MutationKey, string>> = {
  'bin-night': 'Bin night. Watch the wheelies.',
  'magpie-season': "Magpie's nesting in the gum. Wear a hat.",
  sprinkler: "Owner swears the sprinkler's off.",
  bindii: "Bindii's through the lot. Shoes on.",
  'heavy-leaves': "The gum's dropped everything it owns.",
  'above-ground-pool': 'New pool went in. Mind the liner.',
  trampoline: 'Trampoline moved again. Ask the kids where.',
  'veggie-bed': 'New veggie bed. Tomatoes are sacred.',
  ibis: "Bin chickens about. Grab the loose stuff first.",
  'lost-ball': "Kids lost a ball out there. Keep an eye out.",
  'hot-day': "Forty in the shade. Pace yourself.",
};

const OVAL_BLURBS = ['Mind the wicket, mate — that\'s a fifty-dollar mistake.', 'Grand final Saturday. No pressure.', 'Council wants it looking like the MCG. On a council budget.'];

function blurbFor(rng: Rng, layout: Layout, mutations: MutationKey[]): string {
  const mut = mutations.map((m) => MUTATION_BLURBS[m]).filter((b): b is string => !!b);
  if (mut.length && rng() < 0.7) return pick(rng, mut);
  if (layout.zones.some((z) => z.doNotMow) && rng() < 0.6) return OVAL_BLURBS[0];
  if (layout.locationGroup === 'ovals' && rng() < 0.5) return pick(rng, OVAL_BLURBS);
  if (layout.grassType === 'kikuyu' && rng() < 0.4) return GENERIC_BLURBS[0];
  return pick(rng, GENERIC_BLURBS);
}

/** Best (lowest par) owned mower for estimates; falls back to the starter. */
function bestMower(save: SaveGame, models: Record<string, EquipmentModel>, layout: Layout, tasks: TaskSpec[], lostBall: boolean): number {
  const mowers = ownedOfKind(save, 'mower', models).map((o) => models[o.modelKey]).filter(Boolean);
  if (!mowers.length) mowers.push(models['redgum-46'] ?? MODELS['redgum-46']);
  const input = estimateParInput(layout, tasks, lostBall);
  let best = Infinity;
  for (const m of mowers) best = Math.min(best, parSeconds(input, m, tasks));
  return best;
}

/**
 * 4–6 contracts for the location groups the player has unlocked. Tasks only
 * include jobs the player owns the gear for (trim → trimmer; blow → blower or
 * rake; collect → tarp + blower/rake, or a collector mower).
 */
export function generateBoard(
  save: SaveGame, layouts: readonly Layout[], seed: number, models: Record<string, EquipmentModel> = MODELS, opts: BoardOptions = {},
): Contract[] {
  const drawn = opts.taskSubsets ? generateSubsetBoard(save, layouts, seed, models, opts) : generateOriginalBoard(save, layouts, seed, models, opts);
  // Feature P8: a yard worked before gets 1–2 new mutations on top of this draw.
  const board = !opts.revisits || !visitsOf(save).length ? drawn : drawn.map((c) => {
    const base = layouts.find((l) => l.key === c.layoutKey);
    return base ? withRevisit(save, base, c, models, opts) : c;
  });
  if (!opts.neglected) return board;
  // Feature P9: some kept yards haven't been mowed since Easter (after revisits, which re-price).
  return board.map((c) => {
    const base = layouts.find((l) => l.key === c.layoutKey);
    return base ? withNeglect(c, base) : c;
  });
}

function generateOriginalBoard(
  save: SaveGame, layouts: readonly Layout[], seed: number, models: Record<string, EquipmentModel>, opts: BoardOptions,
): Contract[] {
  const rng = mulberry32(mixSeed(seed, 0xb0a4d));
  const groups = unlockedGroups(save.repPoints);
  const pool = layouts.filter((l) => groups.includes(l.locationGroup));
  if (!pool.length) return [];
  const kinds = new Set(save.owned.map((o) => models[o.modelKey]?.kind));
  const collectorMower = save.owned.some((o) => models[o.modelKey]?.collector);
  const told = new Set<string>();
  const canBlow = kinds.has('blower') || kinds.has('rake');
  const canCollect = (kinds.has('tarp') && canBlow) || collectorMower;
  const count = randInt(rng, 4, 6);
  const order = drawYards(rng, pool, count, opts, save, models);
  const out: Contract[] = [];
  for (let i = 0; i < order.length; i++) {
    const base = order[i];
    const cSeed = mixSeed(seed, i + 1);
    const avail = shuffled(rng, base.mutations);
    const roll = rng();
    const nMut = Math.min(avail.length, roll < 0.4 ? 0 : roll < 0.8 ? 1 : 2);
    const mutations = avail.slice(0, nMut);
    const { layout, hazards } = contractLayout(base, { seed: cSeed, mutations });
    const st = layoutStats(layout);
    const hasLeaves = layout.leaves.count[1] > 0;
    const kindsWanted: TaskKind[] = ['mow'];
    if (st.trimCells > 0 && kinds.has('trimmer') && rng() < 0.7) kindsWanted.push('trim');
    if (hasLeaves && st.pavedCells > 0 && canBlow && rng() < 0.5) kindsWanted.push('blow');
    if (hasLeaves && canCollect && rng() < 0.4) kindsWanted.push('collect');
    const wantsGround = rng() < 0.6 || mutations.includes('lost-ball') || mutations.includes('ibis');
    if (wantsGround) kindsWanted.push('ground-check');
    // Complexity from the task list (object count needs complexity, so two passes).
    const draft = kindsWanted.map((k) => makeTask(k, layout));
    const complexity = complexityOf(layout, draft);
    const tasks = kindsWanted.map((k) => makeTask(k, layout, k === 'ground-check' ? complexity + 1 : 0));
    const par = bestMower(save, models, layout, tasks, hazards.lostBall);
    out.push({
      id: `${base.key}-${(cSeed >>> 0).toString(36)}`,
      layoutKey: base.key,
      seed: cSeed,
      mutations,
      tasks,
      complexity,
      feeAud: feeFor(layout, tasks),
      estMinutes: Math.max(1, Math.round(par / 60)),
      blurb: blurbFor(rng, layout, mutations),
      minRepLevel: LOCATION_ORDER.indexOf(base.locationGroup) + 1,
      ...(opts.stories ? { story: storyFor(cSeed, told) } : {}),
    });
  }
  return out;
}

/**
 * The yards for a board of `count` jobs. By default the original rule: spread
 * across the pool before repeating any, so a small pool repeats yards. With
 * `oneJobPerYard`, each yard at most once (and only where `fits` allows it):
 * a small pool makes a smaller board. There, a ground that needs a ride-on
 * the shed hasn't got (shown locked, "Needs a ride-on") doesn't count toward
 * the board's jobs, and at most one is on it, so the jobs you can take are
 * still `count`. Either way one shuffle, so a pool that covers the board with
 * nothing locked draws the same board both ways.
 */
function drawYards(rng: Rng, pool: readonly Layout[], count: number, opts: BoardOptions, save: SaveGame, models: Record<string, EquipmentModel>): Layout[] {
  if (!opts.oneJobPerYard) {
    let order: Layout[] = [];
    while (order.length < count) order = order.concat(shuffled(rng, pool));
    return order.slice(0, count);
  }
  const rideOn = ownsRideOn(save, models);
  const locked = (l: Layout) => !rideOn && RIDE_ON_GROUPS.includes(l.locationGroup);
  const out: Layout[] = [];
  let open = 0;
  let shut = 0;
  for (const l of shuffled(rng, pool)) {
    if (open >= count) break;
    if (out.some((o) => o.key === l.key) || (opts.fits && !opts.fits(l, out))) continue;
    if (locked(l)) {
      if (shut >= 1) continue;
      shut++;
    } else open++;
    out.push(l);
  }
  return out;
}

// ─── Feature P3: task subsets, gear rules, equipment fit ────────────────

export interface BoardOptions {
  /**
   * Every job on the board at a different yard: a yard is never drawn twice,
   * and a pool smaller than the board makes a smaller board (never a clone of
   * a job already on it). Off when absent (the original draw repeats yards).
   * The game passes true.
   */
  oneJobPerYard?: boolean;
  /**
   * With `oneJobPerYard`: may this yard join the ones already drawn? The game
   * uses it to keep the crew's yards, the player's own house and next-door
   * pairs off one board (game/mapPins.ts boardFits).
   */
  fits?: (next: Layout, chosen: readonly Layout[]) => boolean;
  /**
   * Feature P3: draw each job's task list from TASK_SUBSETS (70% include
   * mowing). Off when absent: the original generator, contract for contract.
   * The game passes FEATURES.taskSubsets (on).
   */
  taskSubsets?: boolean;
  /** Feature P3: give every contract a seeded one-line `story`. The game passes FEATURES.contractStory. */
  stories?: boolean;
  /**
   * Feature P6b (subset boards only): with debris rules, a clear or collect
   * whose starting leaves include bed cells is only offered to a shed that
   * owns a rake; otherwise the task is dropped and P3's adapt/re-pick takes
   * over. Off when absent. The game passes FEATURES.debrisRules.
   */
  debrisRules?: boolean;
  /**
   * Feature P8: on a yard in `save.visits`, add 1–2 mutations that weren't
   * there last time (withRevisit) and set `newSince`. Off when absent, and a
   * save with no visits gets the board exactly as before. The game passes
   * FEATURES.revisits.
   */
  revisits?: boolean;
  /**
   * Feature P9: a seeded NEGLECTED_CHANCE of kept yards (backyards, nature
   * strips, parks) with a mow task come up `neglected` at fee ×1.3. Off when
   * absent. The game passes FEATURES.neglectedGrass.
   */
  neglected?: boolean;
}

export type TaskSubset = 'full-service' | 'mow' | 'mow-pickup' | 'mow-collect' | 'tidy-up' | 'clearance';

/**
 * The task lists a contract can carry (brief P3, plus P3b's full service).
 * Pick-up = ground check, weed control = trim, clearance = blow, bagging =
 * collect. Full service also gets a pick-up when the yard has ground objects
 * (FULL_SERVICE_PICKUP of the time, or always with a lost ball or ibis).
 */
export const TASK_SUBSETS: Readonly<Record<TaskSubset, readonly TaskKind[]>> = {
  'full-service': ['mow', 'trim', 'blow'],
  mow: ['mow'],
  'mow-pickup': ['mow', 'ground-check'],
  'mow-collect': ['mow', 'collect'],
  'tidy-up': ['ground-check', 'trim', 'blow'],
  clearance: ['blow'],
};

/** Share of contracts whose task list includes mowing. */
export const MOW_SUBSET_SHARE = 0.7;
/** Weights as a share of all jobs: full service 35%, the other mowing lists 35%, no-mow 30%. */
const MOW_SUBSETS: ReadonlyArray<readonly [TaskSubset, number]> = [['full-service', 35], ['mow-pickup', 15], ['mow', 10], ['mow-collect', 10]];
const NO_MOW_SUBSETS: ReadonlyArray<readonly [TaskSubset, number]> = [['tidy-up', 18], ['clearance', 12]];
/** Chance a full-service job also carries a pick-up (the original generator's ground-check rate). */
export const FULL_SERVICE_PICKUP = 0.6;

/** Share of a layout's leaves expected to land on paved cells (a clearance job needs some). */
export const MIN_PAVED_LEAF_SHARE = 0.2;

/**
 * Can this gear do this task? trim → trimmer; blow → blower or rake;
 * collect → tarp + blower/rake, or a collector mower; mow → a mower;
 * ground check → hands.
 */
export function taskGearOk(kind: TaskKind, gear: readonly EquipmentModel[]): boolean {
  const has = (k: EquipmentModel['kind']) => gear.some((m) => m.kind === k);
  switch (kind) {
    case 'mow': return has('mower');
    case 'trim': return has('trimmer');
    case 'blow': return has('blower') || has('rake');
    case 'collect': return (has('tarp') && (has('blower') || has('rake'))) || gear.some((m) => m.collector);
    case 'ground-check': return true;
  }
  return true;
}

const pavedLeafCache = new WeakMap<Layout, number>();

/** Expected share of leaves spawning on paved cells: leaf areas weighted by size, else the whole yard. */
export function pavedLeafShare(layout: Layout): number {
  const hit = pavedLeafCache.get(layout);
  if (hit !== undefined) return hit;
  const g = buildGrid(layout, mulberry32(0));
  const cm = g.cellM;
  const count = (x0: number, y0: number, x1: number, y1: number): [number, number] => {
    let free = 0;
    let paved = 0;
    for (let y = y0 + cm / 2; y < y1; y += cm) {
      for (let x = x0 + cm / 2; x < x1; x += cm) {
        const i = cellAt(g, x, y);
        if (i < 0 || isSolidType(g.type[i])) continue;
        free++;
        if (g.type[i] === T_PAVED) paved++;
      }
    }
    return [free, paved];
  };
  const areas = layout.leaves.areas ?? [];
  let share = 0;
  const total = areas.reduce((a, r) => a + r.w * r.h, 0);
  if (total > 0) {
    for (const a of areas) {
      const [free, paved] = count(a.x, a.y, a.x + a.w, a.y + a.h);
      if (free > 0) share += ((a.w * a.h) / total) * (paved / free);
    }
  } else {
    const [free, paved] = count(0, 0, g.w * cm, g.h * cm);
    share = free > 0 ? paved / free : 0;
  }
  pavedLeafCache.set(layout, share);
  return share;
}

/** Does this layout have work for the task at all (so it can't start already done)? */
export function layoutHasWork(kind: TaskKind, layout: Layout): boolean {
  const st = layoutStats(layout);
  const leaves = layout.leaves.count[1] > 0;
  switch (kind) {
    case 'mow': return st.mowableCells > 0;
    case 'trim': return st.trimCells > 0;
    case 'blow': return leaves && st.pavedCells > 0 && pavedLeafShare(layout) >= MIN_PAVED_LEAF_SHARE;
    case 'collect': return leaves;
    case 'ground-check': return true;
  }
  return true;
}

/** A subset fits when the player owns gear for every task and the yard has work for every task. */
export function subsetFits(subset: TaskSubset, layout: Layout, gear: readonly EquipmentModel[]): boolean {
  return TASK_SUBSETS[subset].every((k) => taskGearOk(k, gear) && layoutHasWork(k, layout));
}

/** The shed owns gear for every task on the list (a list is never offered without it). */
export function subsetGearOk(subset: TaskSubset, gear: readonly EquipmentModel[]): boolean {
  return TASK_SUBSETS[subset].every((k) => taskGearOk(k, gear));
}

const sameKinds = (a: readonly TaskKind[], b: readonly TaskKind[]) => a.length === b.length && a.every((k) => b.includes(k));

/**
 * P3c: a list adapted to a yard — tasks the yard has no work for are dropped
 * (full service on an oval with no paved debris is mow + trim). Null when the
 * shed lacks the gear for the list, or the adapted list is empty, only a
 * pick-up, or identical to another list (the caller re-picks the list).
 */
export function adaptSubset(subset: TaskSubset, layout: Layout, gear: readonly EquipmentModel[]): TaskKind[] | null {
  if (!subsetGearOk(subset, gear)) return null;
  return keptList(subset, TASK_SUBSETS[subset].filter((k) => layoutHasWork(k, layout)));
}

/** A cut-down list is kept unless it's empty, only a pick-up, or the same as another list. */
function keptList(subset: TaskSubset, kinds: TaskKind[]): TaskKind[] | null {
  if (!kinds.some((k) => k !== 'ground-check')) return null;
  const cut = kinds.length < TASK_SUBSETS[subset].length;
  if (cut && (Object.keys(TASK_SUBSETS) as TaskSubset[]).some((o) => o !== subset && sameKinds(TASK_SUBSETS[o], kinds))) return null;
  return kinds;
}

/** Points of blowing a clearance must leave to do (it can't start within this of its target). */
export const MIN_BLOW_WORK = 5;

/** Weighted draw over the subsets that pass `ok`. */
function drawSubset(rng: Rng, table: ReadonlyArray<readonly [TaskSubset, number]>, fits: (s: TaskSubset) => boolean): TaskSubset | null {
  const ok = table.filter(([s]) => fits(s));
  if (!ok.length) return null;
  let r = rng() * ok.reduce((a, [, w]) => a + w, 0);
  for (const [s, w] of ok) {
    r -= w;
    if (r <= 0) return s;
  }
  return ok[ok.length - 1][0];
}

const NO_MOW_BLURBS = [
  "Grass got done last week. Everything else didn't.",
  "Lawn's fine. Leave the lawn alone.",
  'Owner mows it himself. The rest, not so much.',
  'Someone slipped on the leaves last week. Paths first.',
  "Leaves everywhere. The grass is the one thing that's fine.",
  'Tidy-up only. The mower gets the day off.',
];

/** Mutations that only make sense with a ground check on the list. */
const NEEDS_GROUND_CHECK: readonly MutationKey[] = ['lost-ball', 'ibis'];

/**
 * Feature P3 board: 4–6 contracts. The yards are drawn exactly as the
 * original generator draws them (uniform over unlocked layouts). Each then
 * gets a list from TASK_SUBSETS by weight — 70% with mowing (full service
 * the most common; mow only, + pick-up, + bagging), 30% without (tidy-up,
 * clearance) — among the lists the shed owns gear for, adapted to the yard
 * by dropping tasks it has no work for (adaptSubset). A list that won't
 * adapt is re-picked; with nothing left the job is mow only.
 */
function generateSubsetBoard(
  save: SaveGame, layouts: readonly Layout[], seed: number, models: Record<string, EquipmentModel>, opts: BoardOptions,
): Contract[] {
  const rng = mulberry32(mixSeed(seed, 0xb0a4d));
  const groups = unlockedGroups(save.repPoints);
  const pool = layouts.filter((l) => groups.includes(l.locationGroup));
  if (!pool.length) return [];
  const gear = save.owned.map((o) => models[o.modelKey]).filter((m): m is EquipmentModel => !!m);
  const hasRake = gear.some((m) => m.kind === 'rake');
  const count = randInt(rng, 4, 6);
  const told = new Set<string>();
  const order = drawYards(rng, pool, count, opts, save, models);
  const out: Contract[] = [];
  for (let i = 0; i < order.length; i++) {
    // P3c: the yard first (uniform, as the original generator), then a list by
    // weight, adapted to the yard; a list that won't adapt is re-picked, never the yard.
    const base = order[i];
    const cSeed = mixSeed(seed, i + 1);
    const tried = new Set<TaskSubset>();
    let table = rng() < MOW_SUBSET_SHARE ? MOW_SUBSETS : NO_MOW_SUBSETS;
    const build = (kinds: readonly TaskKind[], mutations: MutationKey[]) => {
      const { layout, hazards } = contractLayout(base, { seed: cSeed, mutations });
      const complexity = complexityOf(layout, kinds.map((k) => makeTask(k, layout)));
      const tasks = kinds.map((k) => makeTask(k, layout, k === 'ground-check' ? complexity + 1 : 0));
      return { layout, hazards, complexity, tasks };
    };
    let job: (ReturnType<typeof build> & { kindsWanted: TaskKind[]; mutations: MutationKey[] }) | null = null;
    while (!job) {
      const s = drawSubset(rng, table, (x) => !tried.has(x) && subsetGearOk(x, gear));
      if (!s) {
        if (table !== MOW_SUBSETS) { table = MOW_SUBSETS; continue; }
        job = { ...build(['mow'], []), kindsWanted: ['mow'], mutations: [] };
        break;
      }
      tried.add(s);
      const core = adaptSubset(s, base, gear);
      if (!core) continue;
      const full = s === 'full-service';
      const ground = core.includes('ground-check') || full;
      const avail = shuffled(rng, base.mutations.filter((m) => ground || !NEEDS_GROUND_CHECK.includes(m)));
      const roll = rng();
      const nMut = Math.min(avail.length, roll < 0.4 ? 0 : roll < 0.8 ? 1 : 2);
      const mutations = avail.slice(0, nMut);
      let kindsWanted: TaskKind[] = [...core];
      if (full && (mutations.some((m) => NEEDS_GROUND_CHECK.includes(m)) || rng() < FULL_SERVICE_PICKUP)) kindsWanted.push('ground-check');
      let built = build(kindsWanted, mutations);
      // The yard's expected leaves said there's clearing to do; this job's actual leaves may not. Drop it then.
      if (kindsWanted.includes('blow') && blowStartPct(base, { seed: cSeed, mutations, ...built }) > TASK_TARGETS.blow - MIN_BLOW_WORK) {
        const kept = keptList(s, kindsWanted.filter((k) => k !== 'blow'));
        if (!kept) continue;
        kindsWanted = kept;
        built = build(kindsWanted, mutations);
      }
      // P6b: leaves in a bed are the rake's job; no rake in the shed, no clear or collect there.
      if (opts.debrisRules && !hasRake && kindsWanted.some((k) => k === 'blow' || k === 'collect')
        && bedStartLeaves(base, { seed: cSeed, mutations, ...built }) > 0) {
        const kept = keptList(s, kindsWanted.filter((k) => k !== 'blow' && k !== 'collect'));
        if (!kept) continue;
        kindsWanted = kept;
        built = build(kindsWanted, mutations);
      }
      job = { ...built, kindsWanted, mutations };
    }
    const { layout, hazards, tasks, complexity, kindsWanted, mutations } = job;
    const par = bestMower(save, models, layout, tasks, hazards.lostBall);
    const mows = kindsWanted.includes('mow');
    let blurb: string;
    if (mows) blurb = blurbFor(rng, layout, mutations);
    else {
      const mut = mutations.map((m) => MUTATION_BLURBS[m]).filter((b): b is string => !!b);
      blurb = mut.length && rng() < 0.5 ? pick(rng, mut) : pick(rng, NO_MOW_BLURBS);
    }
    out.push({
      id: `${base.key}-${(cSeed >>> 0).toString(36)}`,
      layoutKey: base.key,
      seed: cSeed,
      mutations,
      tasks,
      complexity,
      feeAud: feeFor(layout, tasks),
      estMinutes: Math.max(1, Math.round(par / 60)),
      blurb,
      minRepLevel: LOCATION_ORDER.indexOf(base.locationGroup) + 1,
      ...(opts.stories ? { story: storyFor(cSeed, told) } : {}),
    });
  }
  return out;
}

// ─── Feature P8: yards change between visits ────────────────────────────

const REVISIT_SALT = 0x72657669; // 'revi'

/** Would this job still be offerable with these mutations? The board's own clear/bed checks (P3c, P6b). */
function revisitOk(base: Layout, c: Contract, mutations: MutationKey[], gear: readonly EquipmentModel[], opts: BoardOptions): boolean {
  const kinds = c.tasks.map((t) => t.kind);
  const { layout } = contractLayout(base, { seed: c.seed, mutations });
  const complexity = complexityOf(layout, kinds.map((k) => makeTask(k, layout)));
  const probe = { seed: c.seed, mutations, tasks: c.tasks, complexity };
  if (kinds.includes('blow') && blowStartPct(base, probe) > TASK_TARGETS.blow - MIN_BLOW_WORK) return false;
  if (opts.debrisRules && !gear.some((m) => m.kind === 'rake') && kinds.some((k) => k === 'blow' || k === 'collect')
    && bedStartLeaves(base, probe) > 0) return false;
  return true;
}

/**
 * Feature P8: a contract on a yard the save has visited, with 1–2 of the
 * yard's mutations added that weren't on this draw or the last visit
 * (seeded by the contract and the visit count), then fee, ★, minutes and the
 * pick-up count re-worked for the changed yard. A mutation that would make
 * the job unofferable (a clear that starts done, bed leaves with no rake, a
 * lost ball or ibis without a pick-up) is skipped. `newSince` = the added
 * ones (the usual draw already shows in the blurb). First visit (or nothing
 * new left): unchanged.
 */
export function withRevisit(
  save: SaveGame, base: Layout, c: Contract, models: Record<string, EquipmentModel> = MODELS, opts: BoardOptions = {},
): Contract {
  const last = visitOf(save, base.key);
  if (!last) return c;
  const rng = mulberry32(mixSeed(mixSeed(c.seed, REVISIT_SALT), last.n));
  const ground = c.tasks.some((t) => t.kind === 'ground-check');
  const gear = save.owned.map((o) => models[o.modelKey]).filter((m): m is EquipmentModel => !!m);
  const pool = shuffled(rng, base.mutations.filter((m) => !c.mutations.includes(m) && !last.m.includes(m)
    && (ground || !NEEDS_GROUND_CHECK.includes(m))));
  const want = rng() < 0.5 ? 1 : 2;
  const added: MutationKey[] = [];
  for (const m of pool) {
    if (added.length >= want) break;
    if (revisitOk(base, c, [...c.mutations, ...added, m], gear, opts)) added.push(m);
  }
  if (!added.length) return c;
  const mutations = [...c.mutations, ...added];
  const { layout, hazards } = contractLayout(base, { seed: c.seed, mutations });
  const kinds = c.tasks.map((t) => t.kind);
  const complexity = complexityOf(layout, kinds.map((k) => makeTask(k, layout)));
  const tasks = kinds.map((k) => makeTask(k, layout, k === 'ground-check' ? complexity + 1 : 0));
  const par = bestMower(save, models, layout, tasks, hazards.lostBall);
  return { ...c, mutations, tasks, complexity, feeAud: feeFor(layout, tasks), estMinutes: Math.max(1, Math.round(par / 60)), newSince: added };
}

export type EquipmentFit = 'good' | 'slow' | 'short';

/** Fit ratio at or under this is a Good fit; over FIT_SLOW_MAX is Not enough gear. */
export const FIT_GOOD_MAX = 1.25;
export const FIT_SLOW_MAX = 3;
/** Leaves by rake alone: never better than this ratio (Slow going). */
export const RAKE_ONLY_RATIO = 1.5;

export const FIT_LABEL: Record<EquipmentFit, string> = { good: 'Good fit', slow: 'Slow going', short: 'Not enough gear' };

export function fitFor(ratio: number): EquipmentFit {
  if (!(ratio <= FIT_SLOW_MAX)) return 'short';
  return ratio <= FIT_GOOD_MAX ? 'good' : 'slow';
}

/**
 * The mower a job is pitched at: the widest deck × speed in the catalogue at
 * the job's rep tier (a backyard's is the starter push mower, an oval's the Rider).
 */
export function referenceMower(minRepLevel: number, models: Record<string, EquipmentModel> = MODELS): EquipmentModel {
  let best: EquipmentModel = models['redgum-46'] ?? MODELS['redgum-46'];
  let bestRate = 0;
  for (const m of Object.values(models)) {
    if (m.kind !== 'mower' || m.tier > Math.max(1, minRepLevel)) continue;
    const rate = (m.deckCm ?? 46) * (m.speedKmh ?? 4);
    if (rate > bestRate) { best = m; bestRate = rate; }
  }
  return best;
}

/**
 * Equipment fit for a contract with the player's gear. ratio = par with the
 * best owned mower ÷ par with the reference mower — the mow term is
 * area ÷ (deck width × speed) × 1.6, so it's area ÷ deck width vs par.
 * A task with no owned tool is Not enough gear outright. A no-mow job walks,
 * so its ratio is 1, except leaves by rake alone (RAKE_ONLY_RATIO).
 */
export function equipmentFit(
  contract: Contract, base: Layout, gear: readonly EquipmentModel[], models: Record<string, EquipmentModel> = MODELS,
): { fit: EquipmentFit; ratio: number } {
  const tasks = contract.tasks;
  if (tasks.some((t) => !taskGearOk(t.kind, gear))) return { fit: 'short', ratio: Infinity };
  let ratio = 1;
  const mowers = gear.filter((m) => m.kind === 'mower');
  if (tasks.some((t) => t.kind === 'mow') && mowers.length) {
    const { layout, hazards } = contractLayout(base, contract);
    const input = estimateParInput(layout, tasks, hazards.lostBall);
    const ref = parSeconds(input, referenceMower(contract.minRepLevel, models), tasks);
    const own = Math.min(...mowers.map((m) => parSeconds(input, m, tasks)));
    ratio = ref > 0 ? own / ref : 1;
  }
  const leafWork = tasks.some((t) => t.kind === 'blow' || (t.kind === 'collect' && !gear.some((m) => m.collector)));
  if (leafWork && !gear.some((m) => m.kind === 'blower')) ratio = Math.max(ratio, RAKE_ONLY_RATIO);
  return { fit: fitFor(ratio), ratio };
}
